import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import css from './MultiSelect.module.scss';

const MultiSelect = ({
  label,
  value = [],
  onChange,
  options = [],
  placeholder = 'Select options',
  required = false,
  disabled = false,
  error = '',
  name,
  selectedText = 'items',
  onSelectAll,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState('below');
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && wrapperRef.current && dropdownRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - wrapperRect.bottom;
      const spaceAbove = wrapperRect.top;

      setPosition(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'above' : 'below');
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option) => {
    const newValue = value.includes(option.value)
      ? value.filter(v => v !== option.value)
      : [...value, option.value];

    onChange({
      target: {
        name,
        value: newValue
      }
    });
  };

  const filteredOptions = options.filter(option =>
    String(option.label).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOptions = options.filter(opt => value.includes(opt.value));

  const isAllSelected = value.length === options.length;

  const getDisplayValue = () => {
    if (value.length === 0) return placeholder;
    if (value.length <= 5) {
      return selectedOptions.map(opt => opt.label).join(', ');
    }
    return `${value.length} ${selectedText} selected`;
  };

  if (filteredOptions.length === 0 && searchTerm.trim() === '') {
    return null;
  }

  return (
    <div className={css.multiSelectContainer} ref={wrapperRef}>
      {label && (
        <label className={css.label}>
          {label}
          {required && <span className={css.required}>*</span>}
        </label>
      )}
      <div
        className={`${css.select} ${isOpen ? css.open : ''} ${error ? css.error : ''} ${disabled ? css.disabled : ''}`}
        onClick={handleToggle}
        data-error={!!error}
      >
        <div className={css.selectedValue}>
          {getDisplayValue()}
        </div>
        <div className={css.arrow} />
      </div>
      {isOpen && (
        <div
          className={`${css.dropdown} ${position === 'above' ? css.dropdownAbove : ''}`}
          ref={dropdownRef}
        >
          <input
            type="text"
            className={css.searchInput}
            placeholder={`Search ${selectedText}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className={css.options}>
            {onSelectAll &&
              <div
                className={`${css.option} ${css.selectAll} ${isAllSelected ? css.selected : ''}`}
                onClick={onSelectAll}
              >
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={() => {}}
                  className={css.checkbox}
                />
                Select All
              </div>
            }
            {filteredOptions.map(option => (
              <div
                key={option.value}
                className={`${css.option} ${value.includes(option.value) ? css.selected : ''}`}
                onClick={() => handleSelect(option)}
              >
                <input
                  type="checkbox"
                  checked={value.includes(option.value)}
                  onChange={() => {}}
                  className={css.checkbox}
                />
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
      {error && <span className={css.errorMessage}>{error}</span>}
    </div>
  );
};

MultiSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  name: PropTypes.string.isRequired,
  selectedText: PropTypes.string
};

export default MultiSelect;
